import { Check, HighlightOff } from "@mui/icons-material"
import { LinearProgress, Stack, useMediaQuery, useTheme } from "@mui/material"
import { FC, useMemo, useState } from "react"

import {
  AdvancedFormFieldProps,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { CardTable } from "src/components/common/cardTable"
import { Drawer } from "src/components/common/drawer"
import { IconButton } from "src/components/common/iconButton"
import { Table, TableActions, TableData } from "src/components/common/table"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"

import { create } from "src/helpers/bem"

import { deepClone } from "src/helpers/deepClone"
import { deleteWebshopContact } from "src/queriesXRM/account"
import { useAccount } from "src/states/account"
import { useCountries } from "src/states/countries"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"
import { useOptionsets } from "src/states/optionsets"
import {
  mergeProductAttributes,
  useProductAttributes,
} from "src/states/shop/productAttributes"
import { TranslationMessages } from "src/translations"

import styles from "./ManageContactsDialog.module.scss"

const bem = create(styles, "ManageContactsDialog")

export type ManageContactsDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const ManageContactsDialog: FC<ManageContactsDialogProps> = ({
  messages,
  style,
}) => {
  const profileMessages = messages.pages.account.overview.profile
  const theme = useTheme()
  const account = useAccount()
  const countries = useCountries()
  const [loading, setLoading] = useState(false)
  const { languages, salutations } = useOptionsets()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  const { open, selectType, canDelete, selectedContactId } = useDialog(
    "manageContactsDialog",
  ) as Dialogs["manageContactsDialog"]

  const { form } = useProductAttributes()

  const handleDeleteContact = async (e: TableData) => {
    setLoading(true)
    await deleteWebshopContact(e.id as string)
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleSelectContact = async (e: TableData) => {
    const contact = account.webshopContacts?.find(
      (contact) => contact.id === e.id,
    )
    const filledFields = form[0].fields.map((field) => {
      const name = field.field?.split("|")[1]
      if (selectType === "contact") {
        if (field.field?.startsWith(`${selectedContactId}`)) {
          switch (name) {
            case "ContactPersonSalutationTextValue":
              return { ...field, value: contact?.salutation }
            case "ContactPersonFirstnameTextValue":
              return { ...field, value: contact?.firstName }
            case "ContactPersonLastnameTextValue":
              return { ...field, value: contact?.lastName }
            case "ContactPersonEmailTextValue":
              return { ...field, value: contact?.email }
            case "ContactPersonLanguageValue":
              return { ...field, value: contact?.language }
            default:
              return field
          }
        } else {
          return field
        }
      } else {
        switch (name) {
          case "PretixSalutationTextValue":
            return { ...field, value: contact?.salutation }
          case "PretixFirstnameTextValue":
            return { ...field, value: contact?.firstName }
          case "PretixLastnameTextValue":
            return { ...field, value: contact?.lastName }
          case "PretixEmailTextValue":
            return { ...field, value: contact?.email }
          case "PretixEmailLanguageValue":
            return { ...field, value: contact?.language }
          default:
            return field
        }
      }
    })
    const filledForm = deepClone<AdvancedForms>(form[0])
    filledForm.fields = deepClone<AdvancedFormFieldProps[]>(filledFields)

    mergeProductAttributes({
      form: [filledForm],
    })
    closeDialog()
  }

  const formattedContacts = useMemo(
    () =>
      account.webshopContacts?.map((contact) => ({
        name: salutations.find((s) => s.key === contact.salutation)?.value, // quick fix for order
        fullName: contact.firstName + " " + contact.lastName,
        country: countries.find((c) => contact.countryCodeId === c.key)
          ?.countryTag, // use ISO code
        company: contact.company,
        email: contact.email,
        phone: contact.phone,
        language: languages.find((lang) => lang.key === contact.language)
          ?.value,
        id: contact.id,
      })),
    [account.webshopContacts],
  ) as TableData[]

  const tableAction = () => {
    const actions: TableActions[] = []
    if (canDelete === true) {
      actions.push({
        icon: <HighlightOff />,
        color: "error",
        onClick: handleDeleteContact,
      })
    }

    if (selectType) {
      actions.push({
        icon: <Check />,
        color: "primary",
        onClick: handleSelectContact,
      })
    }
    return actions
  }

  const closeDialog = () => {
    mergeDialog("manageContactsDialog", {
      open: false,
      selectType: undefined,
      canDelete: false,
    })
  }

  const renderTable = () => {
    if (isMobile) {
      return renderMobileTable()
    }

    return (
      <Table
        cellTranslations={profileMessages.contacts.manage.table}
        data={formattedContacts}
        iconActions={tableAction()}
        messages={messages}
        order="desc"
        orderBy="name"
      />
    )
  }

  const renderMobileTable = () => {
    const fieldNames = Object.keys(formattedContacts[0]).filter(
      (f) => f !== "children",
    )
    const fieldNameTranslations = profileMessages.contacts.manage.table
    return (
      <CardTable
        getKey={(c, i) => (c.id as string | null | undefined) ?? i}
        items={formattedContacts}
        onCardSelect={
          selectType ? (contact) => handleSelectContact(contact) : undefined
        }
        onDelete={
          canDelete ? (contact) => handleDeleteContact(contact) : undefined
        }
      >
        {(contact: TableData, i: number) => (
          <div className={bem("contact")}>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.name.label}:
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.name}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.fullName.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.fullName as string}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.country.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.country as string}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.company.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.company as string}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.phone.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.phone as string}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.email.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.email as string}
              </span>
            </div>
            <div className={bem("contact__attribute")}>
              <span className={bem("contact__attribute__label")}>
                {fieldNameTranslations.language.label}:{" "}
              </span>
              <span className={bem("contact__attribute__data")}>
                {contact.language as string}
              </span>
            </div>
          </div>
        )}
      </CardTable>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{ ...(isMobile && { width: "100vw" }) }}
      style={style}
      sx={{ zIndex: 1202 }}
      title={profileMessages.contacts.manage.title}
      onClose={() => closeDialog()}
      onOpen={() => mergeDialog("manageContactsDialog", { open: true })}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2>{profileMessages.contacts.manage.title}</h2>
          <div className={bem("close")}>
            <IconButton
              color={"primary"}
              icon={HighlightOff}
              onClick={() => {
                closeDialog()
              }}
            />
          </div>
        </div>
        {loading && <LinearProgress color="primary" />}
        {!loading && (
          <Stack className={bem("content")} direction={"column"} spacing={2}>
            {account.webshopContacts && account.webshopContacts.length > 0 ? (
              renderTable()
            ) : (
              <p className={bem("nodata")}>
                {profileMessages.contacts.manage.noData}
              </p>
            )}
          </Stack>
        )}
      </div>
    </Drawer>
  )
}
